import { apiClient } from './axios';
import { Library, LibraryTier } from '../types/library';
import { CreateLibraryRequest } from '../models/library/createLibraryRequest';
import { UpdateLibraryRequest } from '../models/library/updateLibraryRequest';
import { LibraryFilterParams } from '../models/library/libraryFilterParams';
import { CategoryStatsResponse } from '../models/library/categoryStatsResponse';
import { LanguageLibraryStatsResponse } from '../models/library/languageLibraryStatsResponse.ts';
import {
    GenerateAiWordbookRequest,
    GenerateAiWordbookResponse,
    GenerateAiGroupLibraryRequest,
    GenerateAiGroupLibraryResponse,
} from '../models/library/aiLibraryRequest';
import { PaginationData } from '../models/paginationData';

export const libraryApi = {
    // 목록 조회
    getList: async (params: LibraryFilterParams) => {
        const { page, size, sort, ...filters } = params;

        return apiClient.get<PaginationData<Library>>('/admin/library', {
            page: page - 1,
            size,
            sort: sort || 'id,desc',
            ...filters,
        });
    },

    // 단건 조회
    getOne: async (id: number) => {
        return apiClient.get<Library>(`/admin/library/${id}`);
    },

    // 여러 건 조회
    getMany: async (ids: number[]) => {
        return apiClient.get<Library[]>('/admin/library/batch', {
            ids: ids.join(','),
        });
    },

    // 생성 (단일 단어장)
    createWordbook: async (data: CreateLibraryRequest) => {
        return apiClient.postNoData('/admin/library/wordbook', data);
    },

    // 생성 (그룹 단어장)
    createGroup: async (data: CreateLibraryRequest) => {
        return apiClient.postNoData('/admin/library/group', data);
    },

    // 수정
    update: async (id: number, data: UpdateLibraryRequest) => {
        return apiClient.putNoData(`/admin/library/${id}`, data);
    },

    // 삭제
    delete: async (id: number) => {
        return apiClient.deleteNoData(`/admin/library/${id}`);
    },

    // 노출 토글
    toggleVisibility: async (id: number) => {
        return apiClient.patchNoData(`/admin/library/${id}/visibility`);
    },

    // 등급 변경 (FREE / PREMIUM)
    updateTier: async (id: number, tier: LibraryTier) => {
        return apiClient.patch<{ libraryId: number; tier: LibraryTier }>(
            `/admin/library/${id}/tier`,
            { tier }
        );
    },

    // 통계 갱신 (그룹 단어장)
    refreshStats: async (id: number) => {
        return apiClient.postNoData(`/admin/library/${id}/refresh-stats`);
    },

    // 카테고리별 통계
    getCategoryStats: async (headLang?: string, meaningLang?: string) => {
        return apiClient.get<CategoryStatsResponse>(
            '/admin/library/stats/by-category',
            {
                headLang,
                meaningLang,
            }
        );
    },

    // 언어별 통계
    getLanguageStats: async () => {
        return apiClient.get<LanguageLibraryStatsResponse>(
            '/admin/library/stats/by-language'
        );
    },

    // AI 단어장 생성
    generateAiWordbook: async (data: GenerateAiWordbookRequest) => {
        return apiClient.post<GenerateAiWordbookResponse>(
            '/admin/library/ai/wordbook',
            data
        );
    },

    // AI 그룹 단어장 생성
    generateAiGroupLibrary: async (data: GenerateAiGroupLibraryRequest) => {
        return apiClient.post<GenerateAiGroupLibraryResponse>(
            '/admin/library/ai/group',
            data
        );
    },
};
